import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { fetchLocationConsent, saveLocationConsent } from '../api/locationConsentApi';
import { runtime } from '../utils/runtime';
import { normalizeBrowserPosition, positionStatus, smoothPosition } from './core.mjs';
import { LOOT_FRESH_POSITION_OPTIONS } from './locationPolicy.mjs';
import { waitForEncounterGpsFix } from '../encounters/core.mjs';
import {
  LOCATION_CONSENT_GRANTED,
  LOCATION_CONSENT_REVOKED,
  locationActionMode,
  normalizeLocationConsent,
  normalizePermissionState,
  shouldAutoStartLocation,
} from './locationConsentState.mjs';

const FAST_GEO_OPTIONS = Object.freeze({ enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 4000 });
const GEO_OPTIONS = Object.freeze({ enableHighAccuracy: true, maximumAge: 15000, timeout: 12000 });
const RENDER_THROTTLE_MS = 500;
const PlayerLocationContext = createContext(null);

function errorState(error) {
  if (error?.code === 1) return ['denied', 'Die Standortfreigabe wurde im Browser blockiert. Du kannst sie in den Website-Einstellungen wieder erlauben.'];
  if (error?.code === 3) return ['timeout', 'Die Standortabfrage hat zu lange gedauert. Bitte versuche es erneut.'];
  return ['unavailable', 'Der Standort ist derzeit nicht verfügbar. Bitte prüfe dein GPS-Signal und versuche es später erneut.'];
}

export function PlayerLocationProvider({ children, userId = 0, initialConsent = '' }) {
  const resolvedUserId = Number(userId || runtime.user_id || 0);
  const initialStoredConsent = normalizeLocationConsent(initialConsent || runtime.locationConsent);
  const [position, setPosition] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [recenterToken, setRecenterToken] = useState(0);
  const [consent, setConsent] = useState(initialStoredConsent);
  const [consentSaveState, setConsentSaveState] = useState('idle');
  const [consentSaveError, setConsentSaveError] = useState('');
  const [permissionState, setPermissionState] = useState('unknown');
  const [permissionsSupported, setPermissionsSupported] = useState(null);
  const [freshPositionPending, setFreshPositionPending] = useState(false);
  const watchIdRef = useRef(null);
  const desiredActiveRef = useRef(false);
  const positionRef = useRef(null);
  const consentRef = useRef(initialStoredConsent);
  const userIdRef = useRef(resolvedUserId);
  const requestVersionRef = useRef(0);
  const consentMutationRef = useRef(0);
  const lastPublishRef = useRef(0);
  const savePromiseRef = useRef(null);
  const freshRequestRef = useRef(null);
  const freshRequestVersionRef = useRef(0);
  const fixSubscribersRef = useRef(new Set());
  const encounterFreshRequestRef = useRef(null);

  const clearWatcher = useCallback(() => {
    if (watchIdRef.current !== null && navigator?.geolocation?.clearWatch) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;
  }, []);

  const clearPosition = useCallback(() => {
    positionRef.current = null;
    lastPublishRef.current = 0;
    setPosition(null);
  }, []);

  const publishPosition = useCallback((browserPosition, force = false) => {
    const normalized = normalizeBrowserPosition(browserPosition);
    if (!normalized) return false;
    const now = Date.now();
    const smoothed = smoothPosition(positionRef.current, normalized);
    positionRef.current = smoothed;
    fixSubscribersRef.current.forEach((subscriber) => subscriber(smoothed));
    if (!force && now - lastPublishRef.current < RENDER_THROTTLE_MS) return true;
    lastPublishRef.current = now;
    setPosition(smoothed);
    setStatus(positionStatus(smoothed));
    setError(null);
    return true;
  }, []);

  const handleWatchError = useCallback((watchError) => {
    const [nextStatus, message] = errorState(watchError);
    setStatus(nextStatus);
    setError(message);
    if (nextStatus === 'denied') {
      desiredActiveRef.current = false;
      clearWatcher();
      clearPosition();
      setPermissionState('denied');
    }
  }, [clearPosition, clearWatcher]);

  const startWatcher = useCallback(() => {
    if (watchIdRef.current !== null || consentRef.current !== LOCATION_CONSENT_GRANTED) return false;
    if (typeof window === 'undefined' || !window.isSecureContext) {
      setStatus('unavailable');
      setError('Die Standortfunktion benötigt eine sichere HTTPS-Verbindung.');
      return false;
    }
    if (!navigator?.geolocation?.watchPosition) {
      setStatus('unavailable');
      setError('Dieses Gerät unterstützt keine Standortfunktion.');
      return false;
    }

    setStatus('requesting');
    setError(null);
    try {
      navigator.geolocation.getCurrentPosition(
        (browserPosition) => publishPosition(browserPosition, true),
        () => {},
        FAST_GEO_OPTIONS
      );
      watchIdRef.current = navigator.geolocation.watchPosition(publishPosition, handleWatchError, GEO_OPTIONS);
      return true;
    } catch {
      setStatus('unavailable');
      setError('Die Standortermittlung konnte nicht gestartet werden.');
      watchIdRef.current = null;
      return false;
    }
  }, [handleWatchError, publishPosition]);

  const start = useCallback(() => {
    if (consentRef.current !== LOCATION_CONSENT_GRANTED || permissionState === 'denied') return false;
    desiredActiveRef.current = true;
    return startWatcher();
  }, [permissionState, startWatcher]);

  const stop = useCallback(() => {
    desiredActiveRef.current = false;
    clearWatcher();
    setStatus('idle');
    setError(null);
  }, [clearWatcher]);

  const persistConsent = useCallback((nextConsent) => {
    if (savePromiseRef.current) return savePromiseRef.current;
    consentMutationRef.current += 1;
    setConsentSaveState('saving');
    setConsentSaveError('');
    const promise = saveLocationConsent(nextConsent)
      .then((payload) => {
        const saved = normalizeLocationConsent(payload?.consent);
        if (saved) {
          consentRef.current = saved;
          setConsent(saved);
          runtime.locationConsent = saved;
        }
        setConsentSaveState('saved');
        return true;
      })
      .catch(() => {
        setConsentSaveState('error');
        setConsentSaveError('GPS funktioniert in dieser Sitzung. Die Zustimmung konnte gerade nicht dauerhaft im Konto gespeichert werden.');
        return false;
      })
      .finally(() => { savePromiseRef.current = null; });
    savePromiseRef.current = promise;
    return promise;
  }, []);

  const requestConsent = useCallback(() => {
    if (!(resolvedUserId > 0)) return Promise.resolve(false);
    if (typeof window === 'undefined' || !window.isSecureContext) {
      setStatus('unavailable');
      setError('Die Standortfunktion benötigt eine sichere HTTPS-Verbindung.');
      return Promise.resolve(false);
    }
    if (!navigator?.geolocation?.getCurrentPosition) {
      setStatus('unavailable');
      setError('Dieses Gerät unterstützt keine Standortfunktion.');
      return Promise.resolve(false);
    }

    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    const requestUserId = resolvedUserId;
    setStatus('requesting');
    setError(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (browserPosition) => {
          if (requestVersionRef.current !== requestVersion || userIdRef.current !== requestUserId) {
            resolve(false);
            return;
          }
          const normalizedPosition = normalizeBrowserPosition(browserPosition);
          if (!normalizedPosition || !publishPosition(browserPosition, true)) {
            setStatus('unavailable');
            setError('Der Browser hat keine gültige Position geliefert. Bitte versuche es erneut.');
            resolve(false);
            return;
          }

          consentRef.current = LOCATION_CONSENT_GRANTED;
          setConsent(LOCATION_CONSENT_GRANTED);
          runtime.locationConsent = LOCATION_CONSENT_GRANTED;
          desiredActiveRef.current = true;
          setPermissionState('granted');
          startWatcher();
          void persistConsent(LOCATION_CONSENT_GRANTED);
          setRecenterToken((value) => value + 1);
          resolve(normalizedPosition);
        },
        (positionError) => {
          if (requestVersionRef.current !== requestVersion || userIdRef.current !== requestUserId) {
            resolve(false);
            return;
          }
          handleWatchError(positionError);
          resolve(false);
        },
        GEO_OPTIONS
      );
    });
  }, [handleWatchError, persistConsent, publishPosition, resolvedUserId, startWatcher]);

  const requestFreshPosition = useCallback(() => {
    if (freshRequestRef.current) return freshRequestRef.current;
    if (consentRef.current !== LOCATION_CONSENT_GRANTED) {
      const locationError = new Error('Gib deinen Standort frei, bevor du einen Loot-Ort plünderst.');
      locationError.code = 'gps_off';
      return Promise.reject(locationError);
    }
    if (typeof window === 'undefined' || !window.isSecureContext) {
      const locationError = new Error('Die Standortfunktion benötigt eine sichere HTTPS-Verbindung.');
      locationError.code = 'gps_https_required';
      return Promise.reject(locationError);
    }
    if (!navigator?.geolocation?.getCurrentPosition) {
      const locationError = new Error('Dieses Gerät unterstützt keine Standortfunktion.');
      locationError.code = 'location_unavailable';
      return Promise.reject(locationError);
    }

    const requestVersion = freshRequestVersionRef.current + 1;
    freshRequestVersionRef.current = requestVersion;
    const requestUserId = resolvedUserId;
    setFreshPositionPending(true);
    setError(null);
    if (!positionRef.current) setStatus('requesting');

    const promise = new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (browserPosition) => {
          if (freshRequestVersionRef.current !== requestVersion || userIdRef.current !== requestUserId) {
            const superseded = new Error('Die Standortabfrage wurde beendet.');
            superseded.code = 'gps_superseded';
            reject(superseded);
            return;
          }
          if (!publishPosition(browserPosition, true)) {
            const invalid = new Error('Der Browser hat keine gültige Position geliefert.');
            invalid.code = 'location_unavailable';
            setStatus('unavailable');
            setError(invalid.message);
            reject(invalid);
            return;
          }
          resolve(positionRef.current);
        },
        (positionError) => {
          if (freshRequestVersionRef.current !== requestVersion || userIdRef.current !== requestUserId) {
            const superseded = new Error('Die Standortabfrage wurde beendet.');
            superseded.code = 'gps_superseded';
            reject(superseded);
            return;
          }
          const [nextStatus, message] = errorState(positionError);
          handleWatchError(positionError);
          const locationError = new Error(message);
          locationError.code = nextStatus === 'timeout' ? 'gps_timeout' : nextStatus === 'denied' ? 'gps_denied' : 'location_unavailable';
          reject(locationError);
        },
        LOOT_FRESH_POSITION_OPTIONS
      );
    }).finally(() => {
      if (freshRequestRef.current === promise) freshRequestRef.current = null;
      setFreshPositionPending(false);
    });
    freshRequestRef.current = promise;
    return promise;
  }, [handleWatchError, publishPosition, resolvedUserId]);

  const waitForFreshFix = useCallback((timeoutMs) => {
    if (encounterFreshRequestRef.current) return encounterFreshRequestRef.current;
    if (consentRef.current !== LOCATION_CONSENT_GRANTED) {
      const locationError = new Error('Gib deinen Standort frei, bevor du eine Begegnungsaktion ausführst.');
      locationError.code = 'encounter_gps_missing';
      return Promise.reject(locationError);
    }
    if (typeof window === 'undefined' || !window.isSecureContext || !navigator?.geolocation?.watchPosition) {
      const locationError = new Error('Die Standortfunktion ist für diese Begegnungsaktion nicht verfügbar.');
      locationError.code = 'encounter_gps_missing';
      return Promise.reject(locationError);
    }

    setFreshPositionPending(true);
    setError(null);
    const promise = waitForEncounterGpsFix({
      getCurrentFix: () => positionRef.current,
      subscribe: (subscriber) => {
        fixSubscribersRef.current.add(subscriber);
        return () => fixSubscribersRef.current.delete(subscriber);
      },
      ensureActive: () => {
        desiredActiveRef.current = true;
        startWatcher();
      },
      timeoutMs,
    }).finally(() => {
      if (encounterFreshRequestRef.current === promise) encounterFreshRequestRef.current = null;
      setFreshPositionPending(false);
    });
    encounterFreshRequestRef.current = promise;
    return promise;
  }, [startWatcher]);

  const getLatestVerifiedGpsFix = useCallback(() => positionRef.current, []);

  const revokeConsent = useCallback(async () => {
    requestVersionRef.current += 1;
    freshRequestVersionRef.current += 1;
    stop();
    clearPosition();
    consentRef.current = LOCATION_CONSENT_REVOKED;
    setConsent(LOCATION_CONSENT_REVOKED);
    runtime.locationConsent = LOCATION_CONSENT_REVOKED;
    return persistConsent(LOCATION_CONSENT_REVOKED);
  }, [clearPosition, persistConsent, stop]);

  const refreshPermission = useCallback(async () => {
    if (!navigator?.permissions?.query) {
      setPermissionsSupported(false);
      setPermissionState('unknown');
      return 'unknown';
    }
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      const next = normalizePermissionState(permission.state);
      setPermissionState(next);
      return next;
    } catch {
      setPermissionsSupported(false);
      setPermissionState('unknown');
      return 'unknown';
    }
  }, []);

  const recenter = useCallback(() => setRecenterToken((value) => value + 1), []);

  useEffect(() => {
    userIdRef.current = resolvedUserId;
    requestVersionRef.current += 1;
    freshRequestVersionRef.current += 1;
    consentMutationRef.current += 1;
    desiredActiveRef.current = false;
    clearWatcher();
    clearPosition();
    setStatus('idle');
    setError(null);
    setConsentSaveState('idle');
    setConsentSaveError('');

    const nextConsent = normalizeLocationConsent(initialConsent || runtime.locationConsent);
    consentRef.current = nextConsent;
    setConsent(nextConsent);
    if (!(resolvedUserId > 0)) return undefined;

    let cancelled = false;
    const consentVersion = consentMutationRef.current;
    fetchLocationConsent()
      .then((payload) => {
        if (cancelled || userIdRef.current !== resolvedUserId || consentMutationRef.current !== consentVersion) return;
        const stored = normalizeLocationConsent(payload?.consent);
        consentRef.current = stored;
        setConsent(stored);
        runtime.locationConsent = stored;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [clearPosition, clearWatcher, initialConsent, resolvedUserId]);

  useEffect(() => {
    if (!(resolvedUserId > 0) || !navigator?.permissions?.query) {
      setPermissionsSupported(false);
      setPermissionState('unknown');
      return undefined;
    }

    let cancelled = false;
    let permission = null;
    const onPermissionChange = () => {
      const next = normalizePermissionState(permission?.state);
      setPermissionState(next);
      if (next === 'denied') {
        desiredActiveRef.current = false;
        clearWatcher();
        clearPosition();
        setStatus('denied');
        setError('Die Standortfreigabe wurde im Browser blockiert. Du kannst sie in den Website-Einstellungen wieder erlauben.');
      } else if (next === 'granted' && consentRef.current === LOCATION_CONSENT_GRANTED) {
        desiredActiveRef.current = true;
        startWatcher();
      } else if (next === 'prompt') {
        desiredActiveRef.current = false;
        clearWatcher();
        clearPosition();
        setStatus('idle');
      }
    };

    navigator.permissions.query({ name: 'geolocation' })
      .then((result) => {
        if (cancelled) return;
        permission = result;
        setPermissionsSupported(true);
        setPermissionState(normalizePermissionState(result.state));
        if (typeof result.addEventListener === 'function') result.addEventListener('change', onPermissionChange);
        else result.onchange = onPermissionChange;
      })
      .catch(() => {
        if (!cancelled) {
          setPermissionsSupported(false);
          setPermissionState('unknown');
        }
      });

    return () => {
      cancelled = true;
      if (!permission) return;
      if (typeof permission.removeEventListener === 'function') permission.removeEventListener('change', onPermissionChange);
      else if (permission.onchange === onPermissionChange) permission.onchange = null;
    };
  }, [clearPosition, clearWatcher, resolvedUserId, startWatcher]);

  useEffect(() => {
    if (!shouldAutoStartLocation({
      userId: resolvedUserId,
      consent,
      permissionState,
      permissionsSupported,
    })) return;
    desiredActiveRef.current = true;
    startWatcher();
  }, [consent, permissionState, permissionsSupported, resolvedUserId, startWatcher]);

  useEffect(() => {
    const resumeWatcher = () => {
      if (document.visibilityState === 'hidden') clearWatcher();
      else if (desiredActiveRef.current) startWatcher();
    };
    document.addEventListener('visibilitychange', resumeWatcher);
    window.addEventListener('pageshow', resumeWatcher);
    return () => {
      document.removeEventListener('visibilitychange', resumeWatcher);
      window.removeEventListener('pageshow', resumeWatcher);
    };
  }, [clearWatcher, startWatcher]);

  useEffect(() => () => {
    requestVersionRef.current += 1;
    freshRequestVersionRef.current += 1;
    desiredActiveRef.current = false;
    clearWatcher();
    clearPosition();
  }, [clearPosition, clearWatcher]);

  const secureContext = typeof window !== 'undefined' && window.isSecureContext;
  const geolocationSupported = typeof navigator !== 'undefined' && Boolean(navigator.geolocation?.watchPosition);
  const actionMode = locationActionMode({ consent, permissionState, status, secureContext, geolocationSupported });
  const value = useMemo(() => ({
    position,
    status,
    error,
    start,
    stop,
    requestConsent,
    requestFreshPosition,
    waitForFreshFix,
    getLatestVerifiedGpsFix,
    revokeConsent,
    refreshPermission,
    recenter,
    recenterToken,
    consent,
    consentSaveState,
    consentSaveError,
    permissionState,
    permissionsSupported,
    freshPositionPending,
    actionMode,
    isSecureContext: secureContext,
    isActive: ['active', 'inaccurate', 'requesting'].includes(status),
    watcherActive: watchIdRef.current !== null,
  }), [actionMode, consent, consentSaveError, consentSaveState, error, freshPositionPending, getLatestVerifiedGpsFix, permissionState, permissionsSupported, position, recenter, recenterToken, refreshPermission, requestConsent, requestFreshPosition, revokeConsent, secureContext, start, status, stop, waitForFreshFix]);

  return <PlayerLocationContext.Provider value={value}>{children}</PlayerLocationContext.Provider>;
}

export function usePlayerLocation() {
  const context = useContext(PlayerLocationContext);
  if (!context) throw new Error('usePlayerLocation benötigt den PlayerLocationProvider.');
  return context;
}

export default usePlayerLocation;
